export type TierGrade = 'S' | 'A+' | 'A' | 'B+' | 'B' | 'C' | 'D' | 'EX';

/** Display order of tiers, best first; EX is a separate shelf at the end */
export const TIER_ORDER: TierGrade[] = ['S', 'A+', 'A', 'B+', 'B', 'C', 'D', 'EX'];

export interface MangaSource {
  name: string;
  url: string;
  chapter: number;
  chapterAt?: number; // epoch ms when this site published `chapter`
  /** epoch ms `chapter` was last read from the site's own page; sync keeps the most recently read value */
  checkedAt?: number;
}

export interface Manga {
  id: string;
  title: string;
  grade: TierGrade;
  currentCh: number;
  readCh: number;
  imageUrl: string;
  status: string; // "ยังไม่จบ" | "จบภาค 1" | "จบภาค 2" | "จบภาค 3" | "จบภาค X" | "จบแล้ว"
  nationality?: string; // "🇰🇷" | "🇨🇳" | "🇯🇵" | ""
  order: number;
  sources: MangaSource[];
  sourceUrl?: string; // legacy support
  latestSourceUrl?: string;
  latestSourceName?: string;
  lastUpdatedAt?: string; // "YYYY-MM-DD" or formatted date
  latestChapterAt?: number; // epoch ms the newest chapter first appeared on the fastest site
}

export interface KnownSite {
  name: string;
  url: string;
}

export interface TierInfo {
  grade: TierGrade;
  title: string;
  subtitle: string;
  /** Tinted letter badge used in section headings */
  badge: string;
  /** Solid fill for the selected state in tier pickers */
  solid: string;
}

export const TIER_CONFIGS: Record<TierGrade, TierInfo> = {
  S: {
    grade: 'S',
    title: 'S TIER',
    subtitle: 'ระดับตำนาน',
    badge: 'bg-rose-500/15 text-rose-300 ring-rose-500/30',
    solid: 'bg-rose-500 text-white',
  },
  'A+': {
    grade: 'A+',
    title: 'A+ TIER',
    subtitle: 'โคตรดี',
    badge: 'bg-pink-500/15 text-pink-300 ring-pink-500/30',
    solid: 'bg-pink-500 text-white',
  },
  A: {
    grade: 'A',
    title: 'A TIER',
    subtitle: 'สนุกมาก',
    badge: 'bg-orange-500/15 text-orange-300 ring-orange-500/30',
    solid: 'bg-orange-500 text-white',
  },
  'B+': {
    grade: 'B+',
    title: 'B+ TIER',
    subtitle: 'สนุกดี',
    badge: 'bg-amber-400/15 text-amber-300 ring-amber-400/30',
    solid: 'bg-amber-400 text-zinc-950',
  },
  B: {
    grade: 'B',
    title: 'B TIER',
    subtitle: 'อ่านเพลินๆ',
    badge: 'bg-yellow-300/15 text-yellow-200 ring-yellow-300/30',
    solid: 'bg-yellow-300 text-zinc-950',
  },
  C: {
    grade: 'C',
    title: 'C TIER',
    subtitle: 'พอได้ / ดองไว้',
    badge: 'bg-emerald-500/15 text-emerald-300 ring-emerald-500/30',
    solid: 'bg-emerald-500 text-white',
  },
  D: {
    grade: 'D',
    title: 'D TIER',
    subtitle: 'อ่านฆ่าเวลา',
    badge: 'bg-slate-400/15 text-slate-300 ring-slate-400/30',
    solid: 'bg-slate-500 text-white',
  },
  EX: {
    grade: 'EX',
    title: 'EX TIER',
    subtitle: 'หมวดพิเศษ',
    badge: 'bg-violet-500/15 text-violet-300 ring-violet-500/30',
    solid: 'bg-violet-500 text-white',
  },
};
